import { PLACEMENT_STATUS } from "./constants"

/**
 * Determines the placement status for a floor based on files assigned to it.
 * Checks files where building_zone_id matches the floor (file ownership).
 *
 * @param {string} floorId
 * @param {Array|null} files
 * @returns {"none"|"not-placed"|"placed"}
 */
export function getFloorPlacementStatus(floorId, files) {
  if (!files || files.length === 0) return "none"

  const floorFiles = files.filter(file => file.building_zone_id === floorId)
  if (floorFiles.length === 0) return "none"

  const allPlaced = floorFiles.every(file => file.placement_status === PLACEMENT_STATUS.PLACED)
  return allPlaced ? "placed" : "not-placed"
}

/**
 * Counts floors across all buildings by placement status.
 *
 * @param {Array} buildingItems
 * @returns {{all: number, none: number, "not-placed": number, placed: number}}
 */
export function getBuildingFloorStatusCounts(buildingItems) {
  const counts = { all: 0, none: 0, "not-placed": 0, placed: 0 }

  for (const building of buildingItems) {
    if (!building.floors) continue

    for (const floor of building.floors) {
      counts.all += 1
      if (floor.placementStatus in counts) {
        counts[floor.placementStatus] += 1
      }
    }
  }

  return counts
}

/**
 * Resolves a zone ID (building or floor) to its parent building ID.
 * Returns the zone ID as-is if it matches a building, or the parent
 * building ID if it matches a floor within a building.
 *
 * @param {string|null} zoneId - Zone ID to resolve
 * @param {Array} buildings - Building items with nested floors
 * @returns {string|null} The effective building ID, or the original zoneId if not found
 */
function resolveToBuilding(zoneId, buildings) {
  if (!zoneId) return zoneId
  for (const building of buildings) {
    if (building.id === zoneId) return building.id
    if (building.floors) {
      for (const floor of building.floors) {
        if (floor.id === zoneId) return building.id
      }
    }
  }
  return zoneId
}

/**
 * Builds the API payload for moving a file to a target zone (building or floor).
 * Implements business rule: moving a placed file to a DIFFERENT building
 * clears placement (status reverts to pending, coordinates cleared).
 * Moving within the same building (e.g., building to child floor) preserves placement.
 *
 * @param {Object} file - The file object with id, building_zone_id, placement_status
 * @param {string|null} targetZoneId - Target zone ID (building or floor), or null for Inbox
 * @param {Array} buildings - Building items with nested floors for hierarchy resolution
 * @returns {Object} Update payload for the bulk API
 */
export function buildMovePayload(file, targetZoneId, buildings) {
  const resolvedId = targetZoneId === "inbox" ? null : targetZoneId
  const update = { id: file.id, building_zone_id: resolvedId }

  if (file.placement_status === "placed") {
    if (resolvedId === null) {
      // Moving to inbox always clears placement
      update.placement_status = "pending"
      update.coordinates = null
      update.placed_on_zone_id = null
    } else {
      const currentBuilding = resolveToBuilding(file.building_zone_id, buildings)
      const targetBuilding = resolveToBuilding(resolvedId, buildings)
      if (currentBuilding !== targetBuilding) {
        update.placement_status = "pending"
        update.coordinates = null
        update.placed_on_zone_id = null
      }
    }
  }

  return update
}

/**
 * Filters sidebar items by floor placement status. Inbox always included.
 * Buildings with no matching floors are removed.
 *
 * @param {Array} sidebarItems
 * @param {string} filter
 * @returns {Array}
 */
export function filterBuildingsByFloorStatus(sidebarItems, filter) {
  if (filter === "all") return sidebarItems

  return sidebarItems
    .map(item => {
      if (item.type !== "building") return item

      const matchingFloors = (item.floors || []).filter(floor => floor.placementStatus === filter)
      if (matchingFloors.length === 0) return null

      return { ...item, floors: matchingFloors }
    })
    .filter(Boolean)
}
